import type { SignInResult } from "@capacitor-firebase/authentication";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type {
  AdminUserWithNurses,
  Company,
  JobPosition,
  SocialLoginParams,
  Team,
} from "dutyon-server/types";
import type { Auth, AuthProvider, User as FirebaseUser, UserCredential } from "firebase/auth";
import { useCallback, useMemo } from "react";
import { socialLogin } from "../api/auth.api";
import { refreshAccessToken } from "../api/httpClient";
import { unregisterMyPushDevice } from "../api/push-devices.api";
import { queryKeys } from "../api/query-keys";
import { findAllTeams } from "../api/teams.api";
import { getMe } from "../api/users.api";
import { useAuthStore } from "./authStore";
import { clearSessionDrafts } from "./sessionDrafts";
import { pushSession } from "./pushSession";

export { parseJwt, useAuthStore } from "./authStore";

export interface MeNurse {
  id: string;
  userId: string;
  teamId: string | null;
  companyId: string;
  role: string;
  position: JobPosition | null;
  positionId?: string | null;
  employeeId: string | null;
  joinDate: Date | string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  company?: Company | null;
  team?: Team | null;
}

export type MeData = Omit<AdminUserWithNurses, "nurses"> & {
  nurses: MeNurse[];
};

interface EmailAuthParams {
  email: string;
  password: string;
  name?: string;
}

export interface FirebaseIdentity {
  uid: string;
  idToken: string;
  email: string | null;
  displayName: string | null;
  /** Web identity hint; server must independently verify the ID token. */
  emailVerified?: boolean;
}

const createWebIdentity = async (
  user: FirebaseUser,
  displayName = user.displayName,
): Promise<FirebaseIdentity> => ({
  uid: user.uid,
  idToken: await user.getIdToken(),
  email: user.email,
  displayName,
  emailVerified: user.emailVerified,
});

const createNativeIdentity = async (result: SignInResult): Promise<FirebaseIdentity> => {
  if (!result.user) {
    throw new Error("Firebase 사용자 정보를 확인할 수 없습니다.");
  }

  const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
  const { token } = await FirebaseAuthentication.getIdToken();
  return {
    uid: result.user.uid,
    idToken: token,
    email: result.user.email,
    displayName: result.user.displayName,
  };
};

const signInWithPopupWithDetection = async (
  auth: Auth,
  provider: AuthProvider,
  signInWithPopupFn: (auth: Auth, provider: AuthProvider) => Promise<UserCredential>,
): Promise<UserCredential> => {
  return new Promise<UserCredential>((resolve, reject) => {
    const originalOpen = window.open;
    let popup: Window | null = null;

    // Intercept window.open
    window.open = function (...args) {
      popup = originalOpen.apply(this, args);
      return popup;
    };

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let finished = false;

    signInWithPopupFn(auth, provider)
      .then((result) => {
        finished = true;
        if (intervalId) clearInterval(intervalId);
        resolve(result);
      })
      .catch((error) => {
        finished = true;
        if (intervalId) clearInterval(intervalId);
        reject(error);
      });

    // Restore window.open immediately
    window.open = originalOpen;

    // Start polling the popup's status
    intervalId = setInterval(() => {
      if (finished) {
        if (intervalId) clearInterval(intervalId);
        return;
      }
      if (popup?.closed) {
        if (intervalId) clearInterval(intervalId);
        finished = true;
        interface FirebaseErrorWithCode extends Error {
          code?: string;
        }
        const error = new Error(
          "Firebase: Error (auth/popup-closed-by-user).",
        ) as FirebaseErrorWithCode;
        error.code = "auth/popup-closed-by-user";
        reject(error);
      }
    }, 500);
  });
};

async function isNativeAuthPlatform() {
  const { Capacitor } = await import("@capacitor/core");
  return Capacitor.isNativePlatform();
}

let providerSignOuts = 0;
function requireProviderReady() {
  if (providerSignOuts > 0) {
    throw new Error("이전 로그인 연결을 정리하고 있습니다. 잠시 후 다시 시도해 주세요.");
  }
}

export function useAuth() {
  const store = useAuthStore();
  const queryClient = useQueryClient();

  const logout = useCallback(
    async (options?: { reason?: "session-expired" }) => {
      if (
        store.user?.sub !== useAuthStore.getState().user?.sub ||
        store.token !== useAuthStore.getState().token
      )
        return;
      const draftsCleared = options?.reason === "session-expired" || clearSessionDrafts();
      const sessionToken = store.token;
      pushSession.invalidate();
      store.clearToken();
      queryClient.clear();
      // Remote cleanup cannot hold local credentials/cache hostage. Freeze the original account.
      if (sessionToken) {
        void unregisterMyPushDevice(sessionToken).catch(() => undefined);
      }
      void pushSession.recover();

      providerSignOuts++;
      try {
        if (await isNativeAuthPlatform()) {
          const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
          if (!useAuthStore.getState().token) await FirebaseAuthentication.signOut();
        } else {
          const { auth } = await import("../firebase/firebase");
          if (!useAuthStore.getState().token) await auth.signOut();
        }
      } catch {
        // 원격 Firebase sign-out이 실패해도 로컬 세션과 cache는 반드시 제거합니다.
      } finally {
        providerSignOuts--;
      }
      return { draftsCleared };
    },
    [queryClient, store.clearToken, store.token, store.user?.sub],
  );

  /**
   * 병원 승인이나 팀 변경처럼 서버의 사용자 문맥이 바뀐 직후 토큰을 회전하고
   * 현재 사용자 정보를 다시 조회합니다.
   */
  const refreshSession = useCallback(async () => {
    const refreshToken = useAuthStore.getState().refresh_token;
    if (!refreshToken) {
      await logout({ reason: "session-expired" });
      throw new Error("세션 갱신 정보가 없습니다. 다시 로그인해 주세요.");
    }

    let tokens: Awaited<ReturnType<typeof refreshAccessToken>>;
    try {
      tokens = await refreshAccessToken(refreshToken);
    } catch (error) {
      if (isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0)) {
        await logout({ reason: "session-expired" });
      }
      throw error;
    }
    store.setToken(tokens.access_token, tokens.refresh_token);
    await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
    return tokens;
  }, [logout, queryClient, store.setToken]);

  const profileOwner = store.user?.sub;

  // --- Profile Query (Consolidated from useMe) ---
  const { data: profileData, isFetching: isUserFetching } = useQuery<MeData | null>({
    queryKey: queryKeys.auth.profile(profileOwner),
    queryFn: async () => {
      try {
        return await getMe();
      } catch (error: unknown) {
        if (
          isAxiosError(error) &&
          error.response?.status === 404 &&
          profileOwner === useAuthStore.getState().user?.sub
        ) {
          await logout();
        }
        throw error;
      }
    },
    enabled: !!store.token, // Only fetch if authenticated
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

  const userProfile = profileData?.id === profileOwner ? profileData : null;

  // Determine active nurse profile based on user's current companyId
  const activeNurse =
    userProfile?.nurses?.find((nurse) => nurse.companyId === userProfile.companyId) ??
    userProfile?.nurses?.find((nurse) => nurse.id === store.user?.nurseId);

  // Use the role from the active nurse profile
  const role = activeNurse?.role;

  const isHospitalAdmin = role === "HOSPITAL_ADMIN";
  const isTeamAdmin = role === "TEAM_ADMIN";
  const isSuperAdmin = role === "SUPER_ADMIN";

  const canLoadTeams = !!activeNurse && (isHospitalAdmin || isSuperAdmin || !!activeNurse.teamId);

  const { data: authorizedTeamsData, isFetching: isTeamsFetching } = useQuery({
    queryKey: queryKeys.auth.authorizedTeams(
      profileOwner,
      activeNurse?.companyId,
      activeNurse?.teamId,
    ),
    queryFn: async () => {
      const response = await findAllTeams({ limit: 1000 });
      return response.list as Team[];
    },
    enabled: canLoadTeams,
  });

  const teams: Team[] = useMemo(() => {
    return authorizedTeamsData || [];
  }, [authorizedTeamsData]);

  const isFetchingProfile = isUserFetching || isTeamsFetching;

  const signUpWithEmailMutation = useMutation({
    mutationFn: async ({ email, password, name }: EmailAuthParams) => {
      requireProviderReady();
      const [{ auth }, { createUserWithEmailAndPassword, updateProfile }] = await Promise.all([
        import("../firebase/firebase"),
        import("firebase/auth"),
      ]);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      if (auth.currentUser && name) {
        await updateProfile(auth.currentUser, { displayName: name });
      }
      return createWebIdentity(userCredential.user, name || userCredential.user.displayName);
    },
  });

  const signInWithEmailMutation = useMutation({
    mutationFn: async ({ email, password }: EmailAuthParams) => {
      requireProviderReady();
      const [{ auth }, { signInWithEmailAndPassword }] = await Promise.all([
        import("../firebase/firebase"),
        import("firebase/auth"),
      ]);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return createWebIdentity(userCredential.user);
    },
  });

  const signInWithGoogleMutation = useMutation({
    mutationFn: async () => {
      requireProviderReady();
      if (await isNativeAuthPlatform()) {
        const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
        const result: SignInResult = await FirebaseAuthentication.signInWithGoogle();
        return createNativeIdentity(result);
      } else {
        const [{ auth }, { GoogleAuthProvider, signInWithPopup }] = await Promise.all([
          import("../firebase/firebase"),
          import("firebase/auth"),
        ]);
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopupWithDetection(auth, provider, signInWithPopup);
        return createWebIdentity(result.user);
      }
    },
  });

  const signInWithAppleMutation = useMutation({
    mutationFn: async () => {
      requireProviderReady();
      if (await isNativeAuthPlatform()) {
        const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
        const result: SignInResult = await FirebaseAuthentication.signInWithApple();
        return createNativeIdentity(result);
      } else {
        const [{ auth }, { OAuthProvider, signInWithPopup }] = await Promise.all([
          import("../firebase/firebase"),
          import("firebase/auth"),
        ]);
        const provider = new OAuthProvider("apple.com");
        provider.addScope("email");
        provider.addScope("name");
        const result = await signInWithPopupWithDetection(auth, provider, signInWithPopup);
        return createWebIdentity(result.user);
      }
    },
  });

  const sendVerificationMutation = useMutation({
    mutationFn: async (expectedUid: string) => {
      const [{ auth }, { sendEmailVerification }] = await Promise.all([
        import("../firebase/firebase"),
        import("firebase/auth"),
      ]);
      const user = auth.currentUser;
      if (!user || user.uid !== expectedUid) throw new Error("인증 계정이 변경되었습니다.");
      await sendEmailVerification(user);
    },
  });
  const refreshIdentityMutation = useMutation({
    mutationFn: async (expectedUid: string) => {
      const [{ auth }, { reload }] = await Promise.all([
        import("../firebase/firebase"),
        import("firebase/auth"),
      ]);
      const user = auth.currentUser;
      if (!user || user.uid !== expectedUid) throw new Error("인증 계정이 변경되었습니다.");
      await reload(user);
      if (auth.currentUser?.uid !== expectedUid) throw new Error("인증 계정이 변경되었습니다.");
      await user.getIdToken(true);
      if (auth.currentUser?.uid !== expectedUid) throw new Error("인증 계정이 변경되었습니다.");
      return createWebIdentity(user);
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (email: string) => {
      const normalizedEmail = email.trim().toLowerCase();
      if (await isNativeAuthPlatform()) {
        const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
        await FirebaseAuthentication.sendPasswordResetEmail({ email: normalizedEmail });
        return;
      }

      const [{ auth }, { sendPasswordResetEmail }] = await Promise.all([
        import("../firebase/firebase"),
        import("firebase/auth"),
      ]);
      await sendPasswordResetEmail(auth, normalizedEmail);
    },
  });

  const socialLoginMutation = useMutation({
    mutationFn: async (params: SocialLoginParams) => {
      const response = await socialLogin(params);
      store.setToken(response.access_token, response.refresh_token);
      return response;
    },
  });

  return {
    // State (Store)
    token: store.token,
    setToken: store.setToken,
    clearToken: store.clearToken, // Also exposed via logout, but kept for low-level access

    // User Profile (useMe)
    user: userProfile, // Renamed to simple 'user' to match typical usage, contains profile data
    activeNurse,
    activeCompany: activeNurse?.company ?? null,
    activeTeam: activeNurse?.team ?? null,
    teams,
    isFetching: isFetchingProfile,
    isHospitalAdmin,
    isTeamAdmin,
    isSuperAdmin,

    // Auth Actions
    signUpWithEmail: signUpWithEmailMutation.mutateAsync,
    signInWithEmail: signInWithEmailMutation.mutateAsync,
    signInWithGoogle: signInWithGoogleMutation.mutateAsync,
    signInWithApple: signInWithAppleMutation.mutateAsync,
    socialLogin: socialLoginMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    sendVerificationEmail: sendVerificationMutation.mutateAsync,
    refreshFirebaseIdentity: refreshIdentityMutation.mutateAsync,
    refreshSession,
    logout,

    // Auth Loading States
    loading:
      signUpWithEmailMutation.isPending ||
      signInWithEmailMutation.isPending ||
      signInWithGoogleMutation.isPending ||
      signInWithAppleMutation.isPending ||
      socialLoginMutation.isPending ||
      resetPasswordMutation.isPending ||
      sendVerificationMutation.isPending ||
      refreshIdentityMutation.isPending,
    isResettingPassword: resetPasswordMutation.isPending,

    // Auth Errors
    error:
      signUpWithEmailMutation.error ||
      signInWithEmailMutation.error ||
      signInWithGoogleMutation.error ||
      signInWithAppleMutation.error ||
      resetPasswordMutation.error,
  };
}
